/**
 * Shared scaffold pieces for the bowling block-summary charts.
 *
 * Both `BowlingDayChart` (bar-based: side-by-side and stacked) and
 * `BowlingLoadRatioTrendChart` (area-based: rolling averages) share an
 * identical wrapper around their differing chart cores: phase bands, a today
 * reference line and legend, and the small swatch used in each chart's
 * legend. This module exports those pieces so the two charts only differ in
 * what's actually different (Recharts core, tooltip rows, and chart-specific
 * legend entries).
 */
import { Checkbox, FormControlLabel, Switch, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ReferenceArea, ReferenceLine } from 'recharts';
import { PhaseSpan } from '../types';
import {
  PHASE_BAND_FILL_OPACITY,
  PHASE_LABEL_FILL,
  PHASE_LABEL_FONT_SIZE,
  PHASE_LABEL_INSET,
  TODAY_LINE_COLOR,
  truncateToWidth,
} from './chart-utils';

/**
 * Returns the phase background bands as a list of `<ReferenceArea>` elements,
 * clamped to the chart's first/last day. Phases that fall entirely outside
 * the visible range are skipped. Returned as a plain array so Recharts picks
 * up each element as a direct child of the chart.
 */
export function renderPhaseBands({
  phases,
  firstDay,
  lastDay,
}: {
  phases: PhaseSpan[];
  firstDay: string | undefined;
  lastDay: string | undefined;
}) {
  if (firstDay == null || lastDay == null) return null;
  return phases.flatMap((p) => {
    if (p.endDate < firstDay || p.startDate > lastDay) return [];
    const x1 = p.startDate < firstDay ? firstDay : p.startDate;
    const x2 = p.endDate > lastDay ? lastDay : p.endDate;
    return [
      <ReferenceArea
        key={p.id}
        x1={x1}
        x2={x2}
        fill={p.color}
        fillOpacity={PHASE_BAND_FILL_OPACITY}
        label={renderPhaseLabel(p.name)}
      />,
    ];
  });
}

/**
 * Phase band label: horizontal text centered at the top of the band,
 * truncated with an ellipsis if it can't fit the band's width. Charts are
 * expected to leave headroom above `dataMax` so the label sits in clear space.
 */
const renderPhaseLabel = (name: string) => {
  const PhaseLabel = ({
    viewBox,
  }: {
    viewBox?: { x?: number; y?: number; width?: number; height?: number };
  }) => {
    const vx = viewBox?.x ?? 0;
    const vy = viewBox?.y ?? 0;
    const vw = viewBox?.width ?? 0;
    const available = Math.max(0, vw - PHASE_LABEL_INSET * 2);
    const displayed = truncateToWidth(name, available, PHASE_LABEL_FONT_SIZE);
    return (
      <text
        x={vx + vw / 2}
        y={vy + PHASE_LABEL_INSET}
        fontSize={PHASE_LABEL_FONT_SIZE}
        fontWeight={600}
        fill={PHASE_LABEL_FILL}
        textAnchor="middle"
      >
        <title>{name}</title>
        {displayed}
      </text>
    );
  };
  return <PhaseLabel />;
};

/**
 * Dashed vertical line at `todayIso`. Returns null unless the date falls
 * inside the chart's day range — caller should pass through the result of
 * `computeTodayLine`.
 */
export function renderTodayReferenceLine({
  todayIso,
  showTodayLine,
}: {
  todayIso: string | null;
  showTodayLine: boolean;
}) {
  if (!showTodayLine || todayIso == null) return null;
  return (
    <ReferenceLine
      x={todayIso}
      stroke={TODAY_LINE_COLOR}
      strokeWidth={2}
      strokeDasharray="4 4"
    />
  );
}

/**
 * Small dashed-line swatch + `today` label, for the chart legend. Only render
 * when `computeTodayLine` says the line is on screen.
 */
export const TodayLegendItem = () => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-1.5">
      <svg width={16} height={16} className="shrink-0" aria-hidden>
        <line
          x1={0}
          y1={8}
          x2={16}
          y2={8}
          stroke={TODAY_LINE_COLOR}
          strokeWidth={2}
          strokeDasharray="4 4"
        />
      </svg>
      <Typography variant="body2" className="text-gray-600">
        {t('today')}
      </Typography>
    </div>
  );
};

/**
 * Switch + label used in a chart's legend to toggle an overlay feature
 * (rather than a metric series) on/off — e.g. the load-spike flag. Matches
 * the MUI Switch pattern used on PlanPage for feature toggles.
 */
export const LegendSwitch = ({
  label,
  checked,
  onToggle,
}: {
  label: string;
  checked: boolean;
  onToggle: (checked: boolean) => void;
}) => (
  <div className="flex items-center gap-2">
    <Switch
      size="small"
      checked={checked}
      onChange={(e) => onToggle(e.target.checked)}
      inputProps={{ 'aria-label': label }}
      sx={{ p: 0 }}
    />
    <Typography variant="body2" className="text-gray-600">
      {label}
    </Typography>
  </div>
);

/**
 * Small dashed-line swatch + label, for a dashed reference-line legend entry
 * (e.g. the load-spike flag).
 */
export const DashedLineLegendItem = ({
  color,
  label,
}: {
  color: string;
  label: string;
}) => (
  <div className="flex items-center gap-1.5">
    <svg width={16} height={16} className="shrink-0" aria-hidden>
      <line
        x1={0}
        y1={8}
        x2={16}
        y2={8}
        stroke={color}
        strokeWidth={2}
        strokeDasharray="4 4"
      />
    </svg>
    <Typography variant="body2" className="text-gray-600">
      {label}
    </Typography>
  </div>
);

/**
 * Colored square + label, used in each chart's legend for a metric series.
 */
export const LegendSwatch = ({
  color,
  label,
}: {
  color: string;
  label: string;
}) => (
  <div className="flex items-center gap-1.5">
    <div
      className="size-3 shrink-0 rounded-sm w-[16px] h-[16px]"
      style={{ backgroundColor: color }}
    />
    <Typography variant="body2" className="text-gray-600">
      {label}
    </Typography>
  </div>
);

/**
 * Checkbox + label used in a chart's legend to toggle a series' visibility
 * on/off. `FormControlLabel` gives the checkbox its accessible label
 * association for free (screen readers, larger click/keyboard target) and
 * matches the checkbox+label pattern used elsewhere in the app.
 */
export const LegendCheckbox = ({
  color,
  label,
  checked,
  onToggle,
}: {
  color: string;
  label: string;
  checked: boolean;
  onToggle: (checked: boolean) => void;
}) => (
  <FormControlLabel
    sx={{ m: 0, gap: '6px' }}
    control={
      <Checkbox
        checked={checked}
        onChange={(_, next) => onToggle(next)}
        size="small"
        sx={{
          p: 0,
          color,
          '&.Mui-checked': { color },
          '& .MuiSvgIcon-root': { fontSize: 16 },
        }}
      />
    }
    label={
      <Typography variant="body2" className="text-gray-600">
        {label}
      </Typography>
    }
  />
);
